import React, {
  useEffect,
  useContext,
  useState,
  forwardRef,
  useRef
} from 'react';
import videojs, { VideoJsPlayer } from 'video.js';
import 'video.js/dist/video-js.css';
import { AssetVersionItemDto } from '@api/Api';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import { FabricContext } from '@context/FabricContext';
import { usePlayer } from '@context/PlayerContext';
import ControlBar from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/ControlBar';
import { createPortal } from 'react-dom';

const videoJsOptions = {
  autoplay: false,
  aspectRatio: '16:9',
  fluid: true
};

const VideoCard = forwardRef<
  VideoJsPlayer,
  {
    onReady: () => void;
    onError: () => void;
    asset: AssetVersionItemDto;
    isPreparing: boolean;
    isAssetReady: boolean;
  }
>(function VideoCard(
  { onReady, onError, asset, isPreparing, isAssetReady },
  ref
) {
  const { selectedCommentId, playerTime, setPlayerTime } =
    useAssetViewerState();
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const [_player, setPlayer] = useState<VideoJsPlayer | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const { canvas } = useContext(FabricContext);
  const { registerPlayer } = usePlayer();

  useEffect(() => {
    if (!isAssetReady) return;
    if (!canvas) return;
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;
    const resizeObserver = new ResizeObserver(() => {
      const { width, height } = videoContainer.getBoundingClientRect();

      const scale = width / canvas.getWidth();
      const zoom = canvas.getZoom() * scale;
      canvas.setDimensions({
        width,
        height
      });

      canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
    });
    resizeObserver.observe(videoContainer);

    return () => resizeObserver.disconnect();
  }, [isAssetReady, canvas]);

  useEffect(() => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;
    const videoElement = document.createElement('video');
    videoElement.className = 'video-js';
    videoElement.playsInline = true;
    videoContainer.prepend(videoElement);
    const player = videojs(videoElement, videoJsOptions, function ready() {
      // eslint-disable-next-line react/no-this-in-sfc
      this.on('error', () => {
        if (onError) onError();
      });
      if (ref && 'current' in ref) ref.current = this;
      else if (ref) ref(this);
      if (onReady) onReady();
    });
    playerRef.current = player;
    setPlayer(player);
    registerPlayer(player);
    return () => {
      if (ref && 'current' in ref) ref.current = null;
      else if (ref) ref(null);
      playerRef.current = null;
      setPlayer(null);
      registerPlayer(null);
      player.dispose();
      videoElement.remove();
    };
  }, []);

  useEffect(() => {
    // TODO: this or similar code exists in many places (here, in AssetViewer.tsx and in ControlBar.tsx)
    // We need to do this once and then just pass "currentSource" and "playableSources" as props
    // TBD: not sure if this need to be in AssetViewer.tsx or here.
    // Probably here, because it is responsibility of video player to decide what and when it can play.
    // Then we can just utilize onReady callback to set "isPreparing" in AssetViewer.tsx.
    // In that case pass "isPreparing" as prop is also redundant.
    const player = playerRef.current;
    if (!player) return;
    player.pause();
    if (isPreparing) return;

    if (asset.metadata.type === 'video') {
      const readyProxies = Object.entries(asset.metadata.videoProxies)
        .map(([key, value]) => ({ key: parseInt(key, 10), value }))
        .filter((el) => el.value.status === 'ready')
        .sort((a, b) => b.key - a.key);
      const defaultProxy =
        readyProxies.find((el) => el.key <= 720) ??
        readyProxies.find((el) => el.key <= 1080);
      if (defaultProxy) {
        player.src({
          type: 'video/mp4',
          src: defaultProxy.value.url
        });
      } else if (checkCanPlaySource(asset)) {
        player.src({
          type: asset.mimeType,
          src: asset.sourceUrl
        });
      }
    } else if (asset.metadata.type === 'audio') {
      if (asset.metadata.audioProxy.url) {
        player.src({
          type: 'video/mp4',
          src: asset.metadata.audioProxy.url
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, isPreparing]);

  const transcription =
    asset.metadata.type === 'video' || asset.metadata.type === 'audio'
      ? asset.metadata.transcription
      : null;

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (transcription?.status !== 'ready') return;
    if (!transcription.webVttUrl) return;
    const versionId = asset.id;
    const trackId = `transcription-${versionId}`;
    const persistenceKey = `active-cc-${versionId}`;
    const isActive =
      trackId === (player as any)._closedCaptionsId ||
      localStorage.getItem(persistenceKey) === trackId;
    const track = player.addRemoteTextTrack(
      {
        id: trackId,
        kind: 'subtitles',
        src: transcription.webVttUrl,
        srclang:
          transcription.language.length > 0 ? transcription.language[0] : 'und',
        label: 'Transcription',
        mode: isActive ? 'showing' : 'hidden'
      },
      false
    );
    return () => {
      const currentTrack = player.remoteTextTracks().getTrackById(trackId);
      (player as any)._closedCaptionsId =
        currentTrack?.mode === 'showing' ? trackId : null;
      player.removeRemoteTextTrack(track);
    };
  }, [asset.id, transcription?.webVttUrl]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (selectedCommentId === 'new' && !playerTime[1]) {
      setPlayerTime([player.currentTime(), null]);
      player.pause();
    }
  }, [selectedCommentId]);

  return (
    <div ref={videoContainerRef}>
      {_player &&
        createPortal(
          <ControlBar player={_player} asset={asset} />,
          _player.el()
        )}
      {isPreparing && <PlaybackPreparingLoader isVideo={true} />}
      {isAssetReady && <FabricCanvas />}
    </div>
  );
});

export default VideoCard;
